import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import "./componentTooltip.scss";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const ComponentTooltip = ({
	content,
	children,
	direction = "top",
	delColor = "",
	wrapperClasses = "",
	tooltipCustomClasses = "",
	portalRoot = "tooltip-root",
}) => {
	const [active, setActive] = useState(false);
	const tooltipRef = useRef(null);
	const wrapperRef = useRef(null);
	const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

	const showTip = () => {
		setActive(true);
	};

	const hideTip = () => {
		setActive(false);
	};

	// Create a portal container if it doesn't exist
	useEffect(() => {
		if (!document.getElementById(portalRoot)) {
			const portalElement = document.createElement("div");
			portalElement.id = portalRoot;
			portalElement.className = "tooltip-portal";
			document.body.appendChild(portalElement);
		}

		return () => {
			// Optional cleanup if this is the only tooltip using this portal
			const portalElement = document.getElementById(portalRoot);
			if (portalElement && !portalElement.childElementCount) {
				document.body.removeChild(portalElement);
			}
		};
	}, [portalRoot]);

	// Calculate and update tooltip position
	useEffect(() => {
		if (active && wrapperRef.current) {
			const updatePosition = () => {
				const wrapperRect = wrapperRef.current.getBoundingClientRect();
				const newPosition = { top: 0, left: 0 };

				// Base positioning according to direction
				switch (direction) {
					case "top":
						newPosition.left = wrapperRect.left + wrapperRect.width / 2;
						newPosition.top = wrapperRect.top - 10;
						break;
					case "bottom":
						newPosition.left = wrapperRect.left + wrapperRect.width / 2;
						newPosition.top = wrapperRect.bottom + 10;
						break;
					case "left":
						newPosition.left = wrapperRect.left - 10;
						newPosition.top = wrapperRect.top + wrapperRect.height / 2;
						break;
					case "right":
						newPosition.left = wrapperRect.right + 10;
						newPosition.top = wrapperRect.top + wrapperRect.height / 2;
						break;
					default:
						newPosition.left = wrapperRect.left + wrapperRect.width / 2;
						newPosition.top = wrapperRect.top - 10;
				}

				// Add window scroll position
				newPosition.left += window.scrollX;
				newPosition.top += window.scrollY;

				setTooltipPosition(newPosition);
			};

			// Update position initially and on resize/scroll
			updatePosition();
			window.addEventListener("resize", updatePosition);
			window.addEventListener("scroll", updatePosition);

			return () => {
				window.removeEventListener("resize", updatePosition);
				window.removeEventListener("scroll", updatePosition);
			};
		}
	}, [active, direction]);

	// Get tooltip position styles
	const getTooltipStyles = () => {
		const baseStyles = {
			position: "absolute",
			zIndex: 9999,
		};

		// Add positioning based on direction
		switch (direction) {
			case "top":
				return {
					...baseStyles,
					transform: "translate(-50%, -100%)",
					left: `${tooltipPosition.left}px`,
					top: `${tooltipPosition.top}px`,
				};
			case "bottom":
				return {
					...baseStyles,
					transform: "translate(-50%, 0)",
					left: `${tooltipPosition.left}px`,
					top: `${tooltipPosition.top}px`,
				};
			case "left":
				return {
					...baseStyles,
					transform: "translate(-100%, -50%)",
					left: `${tooltipPosition.left}px`,
					top: `${tooltipPosition.top}px`,
				};
			case "right":
				return {
					...baseStyles,
					transform: "translate(0, -50%)",
					left: `${tooltipPosition.left}px`,
					top: `${tooltipPosition.top}px`,
				};
			default:
				return {
					...baseStyles,
					transform: "translate(-50%, -100%)",
					left: `${tooltipPosition.left}px`,
					top: `${tooltipPosition.top}px`,
				};
		}
	};

	return (
		<>
			<div
				ref={wrapperRef}
				className={`tooltip-wrapper ${wrapperClasses}`}
				onMouseEnter={showTip}
				onMouseLeave={hideTip}
			>
				{children}
			</div>

			{active &&
				document.getElementById(portalRoot) &&
				createPortal(
					<div
						ref={tooltipRef}
						className={`tooltip-tip body-font body-font--small regular ${direction} ${tooltipCustomClasses}`}
						style={getTooltipStyles()}
					>
						<ReactMarkdown
							components={{
								// Rewrite `del`s (`~~like so~~`) to `span` with a red foreground color.
								del(props) {
									const { node, ...rest } = props;

									return (
										<span
											className={delColor !== "color-n300" ? delColor : ""}
											{...rest}
										/>
									);
								},
							}}
							remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
							children={content?.replaceAll("\n", "  \n")}
						/>
					</div>,
					document.getElementById(portalRoot)
				)}
		</>
	);
};

export default ComponentTooltip;
